import React, { useEffect, useState, useRef } from 'react';
import Stack from 'components/common/Stack';
import { Table, Typography, Button, Icon, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import displayErrorNotification from 'utils/displayErrorNotification';
import Toolbar from 'components/common/Toolbar';
import { getCareerCoachReport, getReportById } from 'api/reportsApi';
import CareerCoachReportFilters, { filterCareerCoachReport } from './CareerCoachReportFilters';
import { Parser } from 'json2csv';
import moment from 'moment';
import ButtonRow from 'components/common/ButtonRow';
import useLocalStorage from 'utils/useLocalStorage';
import { defaultCareerCoachReportFilterSettings } from 'types/CareerCoachReportPreferences';
import SavePreferencesButton from 'components/common/SavePreferencesButton';
import { PAGINATION_DEFAULT } from 'utils/pagination';

const CareerCoachReportList = props => {
  const [report, setReport] = useState([]);
  const [responses, setResponses] = useState([]);
  const [filteredResponses, setFilteredResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterSettings, setFilterSettings] = useLocalStorage(
    'CareerCoachReport_FilterSettings',
    defaultCareerCoachReportFilterSettings()
  );
  const [sortSettings, setSortSettings] = useLocalStorage('CareerCoachReport_SortSettings', {
    key: 'tmId',
    order: 'ascend'
  });
  const searchRef = useRef();

  useEffect(() => {
    setLoading(true);
    getCareerCoachReport()
      .then(result => {
        const mapped = result.map(r => ({
          tmName: r.a,
          tmPractice: r.b,
          tmMajor: r.c,
          tmPosition: r.d,
          tmOffice: r.e,
          ccName: r.f,
          ccPractice: r.g,
          ccMajor: r.h,
          ccPosition: r.i,
          ccOffice: r.j,
          tmId: r.k,
          ccId: r.l,
          tmIsCapability: r.m,
          ccIsCapability: r.n
        }));
        setResponses(mapped);
        setFilteredResponses(mapped);

        filterCareerCoachReport(
          {
            onSearch: setFilteredResponses,
            responses: mapped,
            setSettings: setFilterSettings
          },
          {},
          {

            coacheeSearchTerm: filterSettings.tmSearch,
            selectedTMPractices: filterSettings.tmPractice,
            selectedTMMajors: filterSettings.tmMajor,
            selectedTMPositions: filterSettings.tmPosition,
            selectedTMOffices: filterSettings.tmOffice,
            coachSearchTerm: filterSettings.ccSearch,
            selectedCCPractices: filterSettings.ccPractice,
            selectedCCMajors: filterSettings.ccMajor,
            selectedCCPositions: filterSettings.ccPosition,
            selectedCCOffices: filterSettings.ccOffice

          }
        );
      })
      .catch(displayErrorNotification)
      .then(() => {
        setLoading(false);
        if (searchRef.current !== undefined && searchRef.current.input !== null) {
          searchRef.current.focus();
        }
      });
  }, []);

  useEffect(() => {
    setReport(getReportById(1));
  }, []);

  let columns = [
    {
      title: 'Team Member (TM)',
      dataIndex: 'tmName',
      key: 'tmName',
      render: (tmName, record) => (
        <Link to={`/employees/${record.tmId}`}>{tmName}</Link>
      ),
      sorter: (a, b) => a.tmName.toLowerCase().localeCompare(b.tmName.toLowerCase())
    },
    {
      title: 'TM Practice',
      dataIndex: 'tmPractice',
      key: 'tmPractice',
      sorter: (a, b) => (a.tmPractice < b.tmPractice ? -1 : 1)
    },

    {
      title: 'TM Major',
      dataIndex: 'tmMajor',
      key: 'tmMajor',
      render: (tmMajor, row) => {
        if (tmMajor === 'No Major' || row.tmIsCapability === 2) {
          return 'No Major';
        }
        return (
          <>
            {row.tmIsCapability === 1 ? (
              <Tooltip title="Capabilities/Specializations">
                <Icon type="wallet" style={{ marginRight: 6 }}/>
              </Tooltip>
            ) : (
              <Tooltip title="Industries">
                <Icon type="shop" style={{ marginRight: 6 }}/>
              </Tooltip>
            )}
            {tmMajor}
          </>
        );
        },
      sorter: (a, b) => (a.tmMajor < b.tmMajor ? -1 : 1)
    },

    {
      title: 'TM Position',
      dataIndex: 'tmPosition',
      key: 'tmPosition',
      sorter: (a, b) => (a.tmPosition < b.tmPosition ? -1 : 1)

    },
    {

      title: 'TM Office',
      dataIndex: 'tmOffice',
      key: 'tmOffice',
      sorter: (a, b) => (a.tmOffice < b.tmOffice ? -1 : 1)
    },
    {
      title: 'Career Coach (CC)',
      dataIndex: 'ccName',
      key: 'ccName',
      render: (ccName, record) => (
        <Link to={`/employees/${record.ccId}`}>{ccName}</Link>
      ),
      sorter: (a, b) => a.ccName.toLowerCase().localeCompare(b.ccName.toLowerCase())
    },
    {
      title: 'CC Practice',
      dataIndex: 'ccPractice',
      key: 'ccPractice',
      sorter: (a, b) => (a.tmMajor < b.tmMajor ? -1 : 1)
    },
    {
      title: 'CC Major',
      dataIndex: 'ccMajor',
      key: 'ccMajor',
      render: (ccMajor, row) => {
        if (ccMajor === 'No Major' || row.ccIsCapability === 2) {
          return 'No Major';
        }
        return (
          <>
            {row.ccIsCapability === 1 ? (
              <Tooltip title="Capabilities/Specializations">
                <Icon type="wallet" style={{ marginRight: 6 }} />
              </Tooltip>
            ) : (
              <Tooltip title="Industries">
                <Icon type="shop" style={{ marginRight: 6 }} />
              </Tooltip>
            )}
            {ccMajor}
          </>
        );
      },
      sorter: (a, b) => (a.ccMajor < b.ccMajor ? -1 : 1)
    },
    {
      title: 'CC Position',
      dataIndex: 'ccPosition',
      key: 'ccPosition',
      sorter: (a, b) => (a.ccPosition > b.ccPosition ? -1 : 1)
    },
    {
      title: 'CC Office',
      dataIndex: 'ccOffice',
      key: 'ccOffice',
      sorter: (a, b) => (a.ccOffice < b.ccOffice ? -1 : 1)
    }
  ];


  const columnsSortSettings = columns.map(column => {
    if (column.key === sortSettings.key) {
      return { ...column, defaultSortOrder: sortSettings.order };
    }
    return column;
  });
  columns = columnsSortSettings;

  function onChange(_pagination, _filters, sorter) {
    if (Object.keys(sorter).length > 0) {
      setSortSettings({
        key: sorter.columnKey,
        order: sorter.order
      });
    }
  }

  const exportEmployeeCompletion = () => {
    const exportable = filteredResponses
      .sort((a, b) => a.tmName.toLowerCase().localeCompare(b.tmName.toLowerCase()))
      .map(k => ({
        'Team Member (TM)': k.tmName,
        'TM Practice': k.tmPractice,
        'TM Major': k.tmMajor,
        'TM Position': k.tmPosition,
        'TM Office': k.tmOffice,
        'Career Coach (CC)': k.ccName,
        'CC Practice': k.ccPractice,
        'CC Major': k.ccMajor,
        'CC Position': k.ccPosition,
        'CC Office': k.ccOffice
      }));


    const json2CsvParser = new Parser();
    const csv = json2CsvParser.parse(exportable);
    const blob = new Blob([csv], { type: 'text/csv', encoding: 'UTF-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `CareerCoachReport-${moment().format('YYYYMMDD-hhmmss')}.csv`;
    link.click();
  };

  return (
    <Stack>
      <ButtonRow style={{ float: 'right' }}>
        {!loading && (
          <Button icon="download" onClick={exportEmployeeCompletion}>
            Export
          </Button>
        )}
        <SavePreferencesButton
          keys={['CareerCoachReport_FilterSettings', 'CareerCoachReport_SortSettings']}
          disabled={loading}
        />
      </ButtonRow>

      <Typography.Title>{report.name}</Typography.Title>
      <Typography.Title level={3}>{report.description}</Typography.Title>
      <Toolbar>
        <CareerCoachReportFilters
          onSearch={setFilteredResponses}
          responses={responses}
          searchRef={searchRef}
          disabled={loading}
          settings={filterSettings}
          setSettings={setFilterSettings}
        />
        <div>
          <span style={{ margin: '0 10px' }}>
            Showing {filteredResponses.length} of {responses.length} Responses
          </span>
        </div>
      </Toolbar>
      <Table
        pagination={PAGINATION_DEFAULT}
        rowKey={r => r.tmId}
        columns={columns}
        dataSource={filteredResponses}
        loading={loading}
        onChange={onChange}
      />
    </Stack>
  );
};

export default CareerCoachReportList;
